import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, CheckCheck, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 15000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const markAllRead = async () => {
    if (!unreadCount || updating) return;
    setUpdating(true);
    try {
      await api.patch("/notifications/read-all");
      setNotifications((items) => items.map((item) => ({ ...item, is_read: true })));
      setUnreadCount(0);
      window.dispatchEvent(new Event("notifications:refresh"));
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update notifications");
    } finally {
      setUpdating(false);
    }
  };

  const openApplications = async (item) => {
    if (!item.is_read) {
      try {
        await api.patch(`/notifications/${item.notification_id}/read`);
        setNotifications((items) => items.map((entry) =>
          entry.notification_id === item.notification_id ? { ...entry, is_read: true } : entry
        ));
        setUnreadCount((count) => Math.max(0, count - 1));
        window.dispatchEvent(new Event("notifications:refresh"));
      } catch (error) {
        toast.error(error?.response?.data?.detail || "Could not update notification");
        return;
      }
    }
    navigate("/applications?role=applicant");
  };

  return (
    <AppLayout>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Notifications</h1>
          <p className="mt-1 text-sm text-slate-500">
            Updates when a gig poster responds to your application.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={markAllRead}
          disabled={!unreadCount || updating}
          data-testid="notifications-mark-all-read"
        >
          <CheckCheck className="w-4 h-4 mr-2" />
          Mark all as read
        </Button>
      </div>

      {loading ? (
        <div className="mt-8 text-sm text-slate-500">Loading notifications…</div>
      ) : notifications.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <Bell className="mx-auto h-8 w-8 text-slate-400" />
          <h2 className="mt-3 font-semibold text-slate-900">You're all caught up</h2>
          <p className="mt-1 text-sm text-slate-500">Application decisions from gig posters will appear here.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {notifications.map((item) => {
            const accepted = item.status === "accepted";
            const StatusIcon = accepted ? CheckCircle2 : XCircle;
            return (
              <div
                key={item.notification_id}
                className={`flex items-start gap-4 rounded-xl border p-4 ${item.is_read ? "border-slate-200 bg-white" : "border-indigo-200 bg-indigo-50/60"}`}
              >
                <div className={`mt-0.5 rounded-full p-2 ${accepted ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                  <StatusIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-semibold text-slate-900">{item.title}</h2>
                    {!item.is_read && <span className="h-2 w-2 rounded-full bg-indigo-600" aria-label="Unread" />}
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{item.message}</p>
                  <p className="mt-2 text-xs text-slate-400">
                    {item.created_at ? new Date(item.created_at).toLocaleString() : ""}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => openApplications(item)}>
                  View application
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
